import type { ReactNode } from 'react'

import Image from 'next/image'

import { useGsap } from '@/hooks/use-gsap'
import { SplitWords } from '@/components/generals/split-words'
import { Counter } from '@/components/generals/counter'
import { Marquee } from '@/components/generals/marquee'
import { resultStats, skinBenefits } from '@/constants/home'

const BenefitPill = ({ label }: { label: string }): ReactNode => (
    <span className='type-small mx-1.5 rounded-full border border-stone-line bg-shell px-5 py-2.5 whitespace-nowrap text-ink'>
        {label}
    </span>
)

export const Results = (): ReactNode => {
    const scope = useGsap<HTMLElement>(({ gsap, select, scope: root, reduced }) => {
        if (reduced) return

        gsap.from(select('[data-results-heading] .reveal-mask > span'), {
            yPercent: 115,
            duration: 1,
            stagger: 0.045,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 74%' }
        })

        // The hero frame widens open from its centre line.
        const frame = select('[data-results-frame]')[0]

        if (frame) {
            gsap.fromTo(
                frame,
                { clipPath: 'inset(14% 18% 14% 18% round 1rem)' },
                {
                    clipPath: 'inset(0% 0% 0% 0% round 1rem)',
                    duration: 1.5,
                    ease: 'nuvea',
                    scrollTrigger: { trigger: frame, start: 'top 84%' }
                }
            )

            gsap.fromTo(
                frame.querySelector('img'),
                { scale: 1.25 },
                {
                    scale: 1.05,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: frame,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 0.8
                    }
                }
            )
        }

        gsap.from(select('[data-result-stat]'), {
            y: 32,
            opacity: 0,
            duration: 0.9,
            stagger: 0.12,
            ease: 'nuvea',
            scrollTrigger: {
                trigger: select('[data-result-stats]')[0],
                start: 'top 86%'
            }
        })

        select('[data-results-panel]').forEach((panel) => {
            gsap.from(panel, {
                y: 44,
                opacity: 0,
                duration: 1,
                ease: 'nuvea',
                scrollTrigger: { trigger: panel, start: 'top 85%' }
            })
        })

        // Both marquee rows slide in from opposite edges before they start looping.
        gsap.from(select('[data-benefit-row]'), {
            opacity: 0,
            duration: 1.1,
            stagger: 0.12,
            ease: 'nuvea',
            scrollTrigger: {
                trigger: select('[data-benefit-rows]')[0],
                start: 'top 88%'
            }
        })
    })

    return (
        <section
            ref={scope}
            id='results'
            className='shell-x mx-auto w-full max-w-[1760px] py-24 md:py-32'
        >
            <SplitWords
                data-results-heading=''
                as='h2'
                lines={['What twelve weeks', 'actually changed.']}
                className='type-h1 max-w-[16ch] text-ink'
            />

            <div className='mt-12 grid grid-cols-1 gap-10 md:mt-16 lg:grid-cols-[1.1fr_1fr] lg:gap-16'>
                <div
                    data-results-frame=''
                    className='relative aspect-[3/2] w-full overflow-hidden rounded-2xl bg-shell'
                >
                    <Image
                        src='/images/results-portrait.png'
                        alt='A woman with clear, even skin smiling with her hands resting on her cheeks'
                        fill
                        sizes='(min-width: 1024px) 55vw, 100vw'
                        className='object-cover will-change-transform'
                    />
                </div>

                <dl data-result-stats='' className='flex flex-col justify-center gap-10'>
                    {resultStats.map((stat) => (
                        <div
                            key={stat.suffix}
                            data-result-stat=''
                            className='border-t border-stone-line pt-6'
                        >
                            <dt className='type-h1 text-ink'>
                                <Counter
                                    value={stat.value}
                                    decimals={stat.decimals}
                                    suffix={stat.suffix}
                                />
                            </dt>

                            <dd className='type-copy mt-3 max-w-[40ch] text-slate-soft'>
                                {stat.body}
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>

            <div className='mt-16 grid grid-cols-1 gap-6 md:mt-24 lg:grid-cols-2'>
                <div
                    data-results-panel=''
                    className='flex flex-col justify-between gap-8 rounded-2xl bg-sage p-8 md:p-12'
                >
                    <h3 className='type-h2 max-w-[14ch] text-ink'>
                        Led by evidence, not by trend.
                    </h3>

                    <p className='type-copy max-w-[48ch] text-ink/75'>
                        Twelve weeks, 412 volunteers, six skin types, assessed by people with no stake in the
                        outcome. Fewer actives at honest concentrations beat the longer
                        routines they replaced on barrier function, water loss and redness.
                    </p>
                </div>

                <div
                    data-results-panel=''
                    className='relative flex min-h-[320px] flex-col justify-end overflow-hidden rounded-2xl bg-shell p-8 md:p-12'
                >
                    <Image
                        src='/images/process-lab.png'
                        alt='Gloved hands lifting the finished formula from its dish with a steel spatula'
                        fill
                        sizes='(min-width: 1024px) 50vw, 100vw'
                        className='object-cover'
                    />

                    <span
                        aria-hidden='true'
                        className='absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent'
                    />

                    <p className='type-body relative text-cream'>
                        Built by two dermatologists
                    </p>
                </div>
            </div>

            <div className='mt-16 md:mt-24'>
                <h3 className='type-h2 max-w-[14ch] text-ink'>
                    What people noticed.
                </h3>

                <div data-benefit-rows='' className='mt-8 flex flex-col gap-3'>
                    <div data-benefit-row=''>
                        <Marquee duration={46}>
                            {skinBenefits.map((benefit) => (
                                <BenefitPill key={benefit} label={benefit} />
                            ))}
                        </Marquee>
                    </div>

                    <div data-benefit-row=''>
                        <Marquee duration={52} direction='right'>
                            {[...skinBenefits].reverse().map((benefit) => (
                                <BenefitPill key={benefit} label={benefit} />
                            ))}
                        </Marquee>
                    </div>
                </div>
            </div>

            <div
                data-results-panel=''
                className='relative mt-16 grid min-h-[380px] grid-cols-1 overflow-hidden rounded-2xl md:mt-24 lg:min-h-[480px]'
            >
                <Image
                    src='/images/barrier-macro.png'
                    alt='Macro detail of smooth, hydrated skin around the eye'
                    fill
                    sizes='100vw'
                    className='object-cover'
                />

                <span
                    aria-hidden='true'
                    className='absolute inset-0 bg-gradient-to-r from-ink/75 via-ink/35 to-transparent'
                />

                <div className='relative flex flex-col justify-end p-8 md:p-14'>
                    <p className='type-display leading-none text-cream'>
                        <Counter value={5} suffix='x' />
                    </p>

                    <p className='type-body mt-4 max-w-[38ch] text-cream/85'>
                        improvement in barrier resilience by week twelve, which is the difference between skin that
                        reacts and skin that copes.
                    </p>
                </div>
            </div>
        </section>
    )
}
